(function () {
  const ANIMATIONS_ENABLED = true;
  if (!ANIMATIONS_ENABLED) return;

  const prefersReducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // ~28% faster than the original preset timings (durations, delays, staggers).
  const TIMING_SCALE = 0.72;
  function t(seconds) {
    return Math.round(seconds * TIMING_SCALE * 1000) / 1000;
  }

  const readyPromise =
    window.__gsapReadyPromise ||
    new Promise((resolve) => {
      window.__gsapReadyFlush = resolve;
    });
  window.__gsapReadyPromise = readyPromise;
  window.__gsapReady = () => readyPromise;

  function markReady() {
    window.__gsapBooted = true;
    if (typeof window.__gsapReadyFlush === "function") window.__gsapReadyFlush();
  }

  function revealElements(els) {
    els.forEach((el) => {
      el.dataset.gaInit = "true";
      el.dataset.gaAnimated = "true";
    });
  }

  function revealWithoutMotion(root = document) {
    root.querySelectorAll("[data-animate]").forEach((el) => {
      el.dataset.gaInit = "true";
      el.dataset.gaAnimated = "true";
    });
  }

  function getElementScrollState(el) {
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    if (rect.bottom < 0) return "past";
    if (rect.top < vh * 0.92) return "in-view";
    return "below";
  }

  function getScrollTrigger(el) {
    const trigger = el.dataset.animateTrigger || "scroll";
    if (trigger === "drawer-open") return null;
    if (trigger === "in-view") {
      const scrollerSelector = el.dataset.animateScroller;
      const scope = el.closest("cart-drawer") || document;
      const scroller = scrollerSelector ? scope.querySelector(scrollerSelector) : null;
      if (!scroller) return null;
      return { trigger: el, scroller: scroller, start: "top 90%", once: true };
    }
    if (el.dataset.animate === "slide-down") return null;
    const state = getElementScrollState(el);
    if (state === "past") return "past";
    if (state === "in-view") return null;
    return { trigger: el, start: "top 88%", once: true };
  }

  function playFromTo(els, fromVars, toVars, delay, options = {}) {
    const scrollTrigger = options.scrollTrigger || undefined;
    const stagger = options.stagger != null ? options.stagger : toVars.stagger;
    gsap.fromTo(els, fromVars, {
      ...toVars,
      duration: t(toVars.duration),
      delay: t(delay),
      force3D: true,
      immediateRender: true,
      overwrite: "auto",
      scrollTrigger: scrollTrigger,
      stagger: stagger != null ? t(stagger) : stagger,
    });
  }

  const PRESETS = {
    pop: (els, delay, options) => {
      playFromTo(
        els,
        { scale: 0, rotation: -12, opacity: 0 },
        { scale: 1, rotation: 0, opacity: 1, duration: 0.55, ease: "back.out(2.2)", stagger: 0.1, clearProps: "transform,opacity" },
        delay,
        options
      );
    },
    "fade-up": (els, delay, options) => {
      playFromTo(
        els,
        { opacity: 0, y: 32 },
        { opacity: 1, y: 0, duration: 0.75, ease: "power3.out", stagger: 0.1, clearProps: "transform,opacity" },
        delay,
        options
      );
    },
    "slide-left": (els, delay, options) => {
      playFromTo(
        els,
        { x: -70, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.65, ease: "back.out(1.6)", stagger: 0.14, clearProps: "transform,opacity" },
        delay,
        options
      );
    },
    "slide-right": (els, delay, options) => {
      playFromTo(
        els,
        { x: 70, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.65, ease: "back.out(1.6)", stagger: 0.14, clearProps: "transform,opacity" },
        delay,
        options
      );
    },
    "slide-down": (els, delay, options) => {
      playFromTo(
        els,
        { y: -40, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.7, ease: "power3.out", stagger: 0.08, clearProps: "transform,opacity" },
        delay,
        options
      );
    },
    wipe: (els, delay, options) =>
      els.forEach((el) => {
        if (options.scrollTrigger === "past") return;
        gsap.fromTo(
          el,
          { clipPath: "inset(0 100% 0 0 round 1.2rem)", opacity: 0 },
          {
            clipPath: "inset(0 0% 0 0 round 1.2rem)",
            opacity: 1,
            duration: t(1.2),
            ease: "expo.inOut",
            delay: t(delay),
            force3D: true,
            immediateRender: true,
            overwrite: "auto",
            scrollTrigger: options.scrollTrigger || undefined,
            onComplete: () => gsap.set(el, { clearProps: "clipPath,opacity" }),
          }
        );
      }),
    "word-reveal": (els, delay, options) =>
      els.forEach((el) => {
        if (options.scrollTrigger === "past") return;
        const words = splitWords(el);
        if (!words.length) return;
        gsap.set(el, { visibility: "visible" });
        gsap.fromTo(
          words,
          { y: "115%", opacity: 0 },
          {
            y: "0%",
            opacity: 1,
            duration: t(0.7),
            ease: "power4.out",
            stagger: t(0.055),
            delay: t(delay),
            force3D: true,
            immediateRender: true,
            overwrite: "auto",
            scrollTrigger: options.scrollTrigger || undefined,
            onComplete: () => {
              gsap.set(words, { clearProps: "transform,opacity" });
              el.querySelectorAll(".ga-word").forEach((w) => {
                w.replaceWith(w.querySelector(".ga-word-inner") || w);
              });
            },
          }
        );
      }),
  };

  function splitWords(el) {
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    let node;
    while ((node = walker.nextNode())) textNodes.push(node);
    textNodes.forEach((textNode) => {
      const frag = document.createDocumentFragment();
      textNode.textContent.split(/(\s+)/).forEach((chunk) => {
        if (!chunk.trim()) {
          frag.appendChild(document.createTextNode(chunk));
        } else {
          const outer = document.createElement("span");
          outer.className = "ga-word";
          outer.style.cssText = "display:inline-block;overflow:hidden;vertical-align:bottom;";
          const inner = document.createElement("span");
          inner.className = "ga-word-inner";
          inner.style.cssText = "display:inline-block;";
          inner.textContent = chunk;
          outer.appendChild(inner);
          frag.appendChild(outer);
        }
      });
      textNode.replaceWith(frag);
    });
    return [...el.querySelectorAll(".ga-word-inner")];
  }

  function runPreset(type, els, delay = 0, options = {}) {
    const preset = PRESETS[type];
    if (!preset || !els.length) return;
    preset(els, delay, options);
    revealElements(els);
  }

  function initStaggerGroup(parent, { triggers: triggers = null } = {}) {
    const stagger = parseFloat(parent.dataset.animateStagger) || 0.1;
    const children = [...parent.children].filter((el) => {
      if (!el.hasAttribute("data-animate")) return false;
      if (el.dataset.gaAnimated === "true") return false;
      if (!triggers) return true;
      return triggers.includes(el.dataset.animateTrigger || "scroll");
    });
    if (!children.length) return;
    const groups = {};
    children.forEach((el) => {
      const type = el.dataset.animate;
      if (!groups[type]) groups[type] = [];
      groups[type].push(el);
    });
    Object.entries(groups).forEach(([type, els]) => {
      const delay = parseFloat(els[0].dataset.animateDelay) || parseFloat(parent.dataset.animateDelay) || 0;
      const scrollTrigger = getScrollTrigger(els[0]);
      if (scrollTrigger === "past") {
        revealElements(els);
        return;
      }
      runPreset(type, els, delay, { stagger: stagger, scrollTrigger: scrollTrigger });
    });
  }

  function initElement(el) {
    if (el.dataset.gaAnimated === "true") return;
    const type = el.dataset.animate;
    const delay = parseFloat(el.dataset.animateDelay) || 0;
    const scrollTrigger = getScrollTrigger(el);
    if (scrollTrigger === "past") {
      revealElements([el]);
      return;
    }
    runPreset(type, [el], delay, { scrollTrigger: scrollTrigger });
  }

  function initRoot(root = document, { triggers: triggers = null } = {}) {
    if (prefersReducedMotion() || !window.gsap) {
      revealWithoutMotion(root);
      return;
    }
    root.querySelectorAll("[data-animate-stagger]").forEach((parent) => {
      if (triggers) {
        const hasMatchingChild = [...parent.children].some(
          (el) => el.hasAttribute("data-animate") && triggers.includes(el.dataset.animateTrigger)
        );
        if (!hasMatchingChild) return;
      } else if (parent.closest("cart-drawer")) {
        return;
      }
      initStaggerGroup(parent, { triggers: triggers });
    });
    const selector = triggers
      ? triggers.map((t) => `[data-animate-trigger="${t}"][data-animate]`).join(",")
      : '[data-animate]:not([data-animate-stagger] > [data-animate]):not([data-animate-trigger="drawer-open"]):not([data-animate-trigger="in-view"]):not(cart-drawer [data-animate])';
    root.querySelectorAll(selector).forEach((el) => {
      initElement(el);
    });
  }

  function resetRoot(root = document) {
    if (!window.gsap) return;
    root.querySelectorAll("[data-animate]").forEach((el) => {
      if (window.ScrollTrigger) {
        ScrollTrigger.getAll().forEach((st) => {
          if (st.trigger === el) st.kill();
        });
      }
      gsap.killTweensOf(el);
      gsap.set(el, { clearProps: "transform,opacity,clipPath,visibility" });
      delete el.dataset.gaAnimated;
      delete el.dataset.gaInit;
    });
  }

  function initScope(root = document) {
    if (prefersReducedMotion() || window.__gsapSkipEnter || !window.gsap) {
      revealWithoutMotion(root);
      return;
    }
    initRoot(root);
    if (window.ScrollTrigger) ScrollTrigger.refresh();
  }

  function canAnimate() {
    return !prefersReducedMotion() && !window.__gsapSkipEnter && window.gsap && window.ScrollTrigger;
  }

  function initPage() {
    if (!canAnimate()) {
      document.documentElement.classList.add("gsap-skip-enter");
      revealWithoutMotion(document);
      markReady();
      return;
    }
    gsap.registerPlugin(ScrollTrigger);
    initRoot(document);
    ScrollTrigger.refresh();
    markReady();
  }

  function navigationType() {
    const entry = performance.getEntriesByType && performance.getEntriesByType("navigation")[0];
    return entry ? entry.type : "navigate";
  }

  function scheduleInit() {
    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      requestAnimationFrame(() => requestAnimationFrame(initPage));
    };

    const restored =
      navigationType() === "reload" || navigationType() === "back_forward" || (window.scrollY || 0) > 0;

    const arm = () => {
      if (restored) {
        requestAnimationFrame(start);
        window.addEventListener("pageshow", start, { once: true });
      } else {
        start();
      }
    };

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", arm, { once: true });
    } else {
      arm();
    }

    window.addEventListener("load", () => {
      if (window.ScrollTrigger) ScrollTrigger.refresh();
    });
    window.addEventListener("pageshow", () => {
      if (window.ScrollTrigger) ScrollTrigger.refresh();
    });
  }

  window.__gsapAnimate = {
    init: (root) => initRoot(root, { triggers: ["drawer-open", "in-view"] }),
    reset: resetRoot,
    initPage: () => initScope(document),
    initScope: initScope,
    resetPage: () => resetRoot(document),
    revealWithoutMotion: revealWithoutMotion,
  };

  scheduleInit();

  document.addEventListener("shopify:section:load", (event) => {
    const root = event.target || document;
    if (canAnimate()) initScope(root);
    else revealWithoutMotion(root);
  });
})();
